/**
 * Portal shell routing — keeps broker / shipper / carrier UIs isolated.
 * Uses COMPANY_USER_FIRESTORE_USER_TYPES (mirrored in backend/src/config/companyUserFirestoreSecurity.ts).
 */

import {
  COMPANY_USER_FIRESTORE_USER_TYPES,
} from '../constants/companyUserFirestoreSecurity';

export type PortalShell = 'shipper' | 'broker' | 'carrier';

export type PortalProductUserType = 'broker' | 'shipper' | 'carrier';

/** Post-login and layout-guard home routes (main app). */
export const PORTAL_HOME_PATHS: Record<PortalProductUserType, string> = {
  broker: '/broker',
  shipper: '/shipper/dashboard',
  carrier: '/carrier/home',
};

export function normalizeProductUserType(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/** True when account should use the carrier portal shell (CarrierLayout). */
export function isCarrierPortalUser(userData: Record<string, unknown>): boolean {
  const ut = normalizeProductUserType(userData.userType);
  // Broker-carrier orgs stay broker-primary; carrier ops use a separate carrier login later.
  return ut === COMPANY_USER_FIRESTORE_USER_TYPES.CARRIER;
}

export function resolvePortalProductUserType(
  userData: Record<string, unknown> | null | undefined
): PortalProductUserType | null {
  if (!userData) return null;
  if (isCarrierPortalUser(userData)) return 'carrier';

  const ut = normalizeProductUserType(userData.userType);
  if (ut === COMPANY_USER_FIRESTORE_USER_TYPES.SHIPPER) return 'shipper';
  if (
    ut === COMPANY_USER_FIRESTORE_USER_TYPES.BROKER ||
    ut === COMPANY_USER_FIRESTORE_USER_TYPES.BROKER_CARRIER
  ) {
    return 'broker';
  }
  return null;
}

/**
 * When user is on the wrong portal shell, return the path they should be sent to.
 * Returns null when the current shell matches their account type.
 */
export function getPortalRedirectForShell(
  shell: PortalShell,
  userData: Record<string, unknown>
): string | null {
  const productType = resolvePortalProductUserType(userData);
  if (!productType) return '/login';

  const home = PORTAL_HOME_PATHS[productType];
  if (shell === 'carrier' && productType === 'carrier') return null;
  if (shell === 'shipper' && productType === 'shipper') return null;
  if (shell === 'broker' && productType === 'broker') return null;
  return home;
}

export function getPortalHomePathForProductType(productType: PortalProductUserType): string {
  return PORTAL_HOME_PATHS[productType];
}
